import { useEffect, type ComponentProps, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { SITE } from "@/lib/site";

type Variant = "primary" | "secondary" | "ghost" | "accent";
type Size = "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition-all duration-300 ease-smooth select-none whitespace-nowrap cursor-pointer";

const variants: Record<Variant, string> = {
  primary:
    "bg-ink text-white hover:bg-ink-soft hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.6)]",
  secondary:
    "bg-transparent text-ink border border-ink/20 hover:border-ink hover:-translate-y-0.5",
  ghost: "bg-transparent text-ink hover:bg-ink/5",
  accent:
    "bg-accent text-ink hover:bg-accent-dim hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(255,214,0,0.8)]",
};

const sizes: Record<Size, string> = {
  md: "h-11 px-5 text-sm",
  lg: "h-14 px-7 text-base",
};

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const STYLES_HREF = "https://assets.calendly.com/assets/external/widget.css";

type CalendlyWindow = Window & {
  Calendly?: { initPopupWidget: (opts: { url: string }) => void };
};

export function openCalendly() {
  loadCalendly()
    .then(() => {
      const w = window as CalendlyWindow;
      if (w.Calendly) {
        w.Calendly.initPopupWidget({ url: SITE.calendly });
      } else {
        window.open(SITE.calendly, "_blank", "noopener,noreferrer");
      }
    })
    .catch(() => {
      window.open(SITE.calendly, "_blank", "noopener,noreferrer");
    });
}

export function usePrefetchCalendly() {
  useEffect(() => {
    loadCalendly().catch(() => {});
  }, []);
}

function loadCalendly(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const w = window as CalendlyWindow;
  if (w.Calendly) return Promise.resolve();

  if (!document.querySelector(`link[href="${STYLES_HREF}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = STYLES_HREF;
    document.head.appendChild(link);
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      if ((w as CalendlyWindow).Calendly) return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

type Props = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
} & Omit<ComponentProps<"button">, "type">;

export function CalendlyButton({
  children,
  variant = "primary",
  size = "md",
  className,
  onClick,
  ...rest
}: Props) {
  usePrefetchCalendly();

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    openCalendly();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {children}
      <ArrowIcon />
    </button>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M2.5 7h9m0 0L7.5 3m4 4l-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
